import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submitFeedback, resetFeedback } from '../redux_files/actions/feedback_actions';
import { showLoader, hideLoader } from '../redux_files/actions/loaderActions';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const { submitting, submitted, error: submitError } = useSelector((s) => s.feedback || { submitting: false, submitted: false, error: '' });

  /*
  // OTP states for phone (disabled)
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
  const [phoneOtpError, setPhoneOtpError] = useState('');

  // OTP states for email (disabled)
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');
  const [emailOtpVerified, setEmailOtpVerified] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState('');
  */

  // Captcha (simple math)
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');

  /* // Simulated OTP for demo (disabled)
  const SIMULATED_OTP = '123456';
  */

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^[6-9]\d{9}$/.test(formData.phone.trim())) errors.phone = 'Enter a valid 10-digit Indian number';
    if (!formData.message.trim()) errors.message = 'Message is required';
    else if (formData.message.trim().length < 30) errors.message = 'Message must be at least 30 characters';
    // Captcha validation
    if (!captchaInput.trim()) {
      errors.captcha = 'Please solve the captcha';
    } else if (parseInt(captchaInput, 10) !== parseInt(captchaAnswer, 10)) {
      errors.captcha = 'Captcha answer is incorrect';
    }
    // OTP verification disabled
    // if (!phoneOtpVerified) errors.phone = 'Please verify your phone number';
    // if (!emailOtpVerified) errors.email = 'Please verify your email';
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Hide "required" error once user enters 3+ characters for specific fields
    if (['name', 'email', 'phone'].includes(name)) {
      if (value.trim().length >= 3) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      } else if (value.trim().length === 0) {
        // Show required message when emptied
        setErrors((prev) => ({
          ...prev,
          [name]: name === 'name' ? 'Name is required' : name === 'email' ? 'Email is required' : 'Phone number is required',
        }));
      }
    }

    // Live email format validation while typing
    if (name === 'email') {
      const emailPattern = /\S+@\S+\.\S+/;
      if (value.trim().length > 0 && !emailPattern.test(value)) {
        setErrors((prev) => ({ ...prev, email: 'Email is invalid' }));
      } else if (emailPattern.test(value)) {
        setErrors((prev) => ({ ...prev, email: undefined }));
      }
    }

    // Live 10-digit Indian phone validation while typing
    if (name === 'phone') {
      const indianPhonePattern = /^[6-9]\d{9}$/;
      const trimmed = value.trim();
      if (trimmed.length > 0 && !indianPhonePattern.test(trimmed)) {
        setErrors((prev) => ({ ...prev, phone: 'Enter a valid 10-digit Indian number' }));
      } else if (indianPhonePattern.test(trimmed)) {
        setErrors((prev) => ({ ...prev, phone: undefined }));
      }
    }

    // Live message length validation while typing (min 30 chars)
    if (name === 'message') {
      const trimmed = value.trim();
      if (trimmed.length === 0) {
        setErrors((prev) => ({ ...prev, message: 'Message is required' }));
      } else if (trimmed.length < 30) {
        setErrors((prev) => ({ ...prev, message: 'Message must be at least 30 characters' }));
      } else {
        setErrors((prev) => ({ ...prev, message: undefined }));
      }
    }

    // Live captcha validation while typing
    if (name === 'captcha') {
      const trimmed = value.trim();
      if (trimmed.length === 0) {
        setErrors((prev) => ({ ...prev, captcha: 'Please solve the captcha' }));
      } else if (parseInt(trimmed, 10) === parseInt(captchaAnswer, 10)) {
        setErrors((prev) => ({ ...prev, captcha: undefined }));
      } else {
        setErrors((prev) => ({ ...prev, captcha: 'Captcha answer is incorrect' }));
      }
    }
  };

  /* // Phone OTP handlers (disabled)
  const handleSendPhoneOtp = () => { };
  const handleVerifyPhoneOtp = () => { };
  */

  /* // Email OTP handlers (disabled)
  const handleSendEmailOtp = () => { };
  const handleVerifyEmailOtp = () => { };
  */

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      dispatch(showLoader());
      dispatch(submitFeedback(formData));
    }
  };

  React.useEffect(() => {
    if (submitted) {
      dispatch(hideLoader());
      setFormData({ name: '', email: '', phone: '', message: '' });
      // OTP reset disabled
      // setPhoneOtpSent(false);
      // setPhoneOtp('');
      // setPhoneOtpVerified(false);
      // setEmailOtpSent(false);
      // setEmailOtp('');
      // setEmailOtpVerified(false);

      // Regenerate captcha after successful submit
      const a = Math.floor(Math.random() * 10) + 1;
      const b = Math.floor(Math.random() * 10) + 1;
      setCaptchaQuestion(`${a} + ${b} = ?`);
      setCaptchaAnswer(String(a + b));
      setCaptchaInput('');
    }
  }, [submitted, dispatch]);

  // Hide loader on error
  React.useEffect(() => {
    if (submitError) {
      dispatch(hideLoader());
    }
  }, [submitError, dispatch]);

  // Clear feedback messages when component unmounts or on first mount
  React.useEffect(() => {
    // On mount, ensure previous messages are cleared
    dispatch(resetFeedback());
    // Initialize captcha on mount
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion(`${a} + ${b} = ?`);
    setCaptchaAnswer(String(a + b));
    return () => {
      // On unmount/navigation away, clear any messages
      dispatch(resetFeedback());
    };
  }, [dispatch]);

  return (
    <div className="contact-container">
      <h2>Contact Us</h2>
      <form onSubmit={handleSubmit} noValidate>
        <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: '15px' }}>
          <div style={{ flex: 0.2, flexDirection: 'column', display: 'flex' }}>
            <label htmlFor="name">Name</label>
          </div>
          <div style={{ flex: '1 1 0', minWidth: 0, flexDirection: 'column', display: 'flex' }}>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'input-error' : ''}
              style={{ width: '90%' }}
            />
            {errors.name && <div className="error-message">{errors.name}</div>}
          </div>
        </div>
        <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: '15px' }}>
          <div style={{ flex: 0.2, flexDirection: 'column', display: 'flex' }}>
            <label htmlFor="email">Email</label>
          </div>
          <div style={{ flex: '1 1 0', minWidth: 0, flexDirection: 'column', display: 'flex' }}>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'input-error' : ''}
              // disabled by OTP verification (disabled)
              // disabled={emailOtpVerified}
              placeholder="example@mail.com"
              style={{ width: '90%' }}
            />
            {errors.email && <div className="error-message">{errors.email}</div>}
          </div>
          {/* Email OTP Send button disabled
          <div style={{ flex: 0.25, flexDirection: 'column', display: 'flex' }}>
            <button type="button" onClick={handleSendEmailOtp} disabled={emailOtpSent || emailOtpVerified} className="otp-button">Send OTP</button>
          </div>
          */}
        </div>
        {/* Email OTP input and Verify button disabled
        {emailOtpSent && !emailOtpVerified && (
          <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: '15px' }}>
            <div style={{ flex: 0.2, flexDirection: 'column', display: 'flex' }}>
              <label htmlFor="emailOtp">Email OTP</label>
            </div>
            <div style={{ flex: '1 1 0', minWidth: 0, flexDirection: 'column', display: 'flex' }}>
              <input id="emailOtp" name="emailOtp" type="text" value={emailOtp} onChange={(e) => setEmailOtp(e.target.value)} maxLength={6} className={emailOtpError ? 'input-error' : ''} style={{ width: '90%' }} />
              {emailOtpError && <div className="error-message">{emailOtpError}</div>}
            </div>
            <div style={{ flex: 0.25, flexDirection: 'column', display: 'flex' }}>
              <button type="button" onClick={handleVerifyEmailOtp} className="otp-button">Verify OTP</button>
            </div>
          </div>
        )}
        */}
        <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: '15px' }}>
          <div style={{ flex: 0.2, flexDirection: 'column', display: 'flex' }}>
            <label htmlFor="phone">Phone</label>
          </div>
          <div style={{ flex: '1 1 0', minWidth: 0, flexDirection: 'column', display: 'flex' }}>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              className={errors.phone ? 'input-error' : ''}
              // disabled by OTP verification (disabled)
              // disabled={phoneOtpVerified}
              placeholder="10-digit number"
              style={{ width: '90%' }}
            />
            {errors.phone && <div className="error-message">{errors.phone}</div>}
          </div>
          {/* Phone OTP Send button disabled
          <div style={{ flex: 0.25, flexDirection: 'column', display: 'flex' }}>
            <button type="button" onClick={handleSendPhoneOtp} disabled={phoneOtpSent || phoneOtpVerified} className="otp-button">Send OTP</button>
          </div>
          */}
        </div>
        {/* Phone OTP input and Verify button disabled
        {phoneOtpSent && !phoneOtpVerified && (
          <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: '15px' }}>
            <div style={{ flex: 0.2, flexDirection: 'column', display: 'flex' }}>
              <label htmlFor="phoneOtp">Phone OTP</label>
            </div>
            <div style={{ flex: '1 1 0', minWidth: 0, flexDirection: 'column', display: 'flex' }}>
              <input id="phoneOtp" name="phoneOtp" type="text" value={phoneOtp} onChange={(e) => setPhoneOtp(e.target.value)} maxLength={6} className={phoneOtpError ? 'input-error' : ''} style={{ width: '90%'}} />
              {phoneOtpError && <div className="error-message">{phoneOtpError}</div>}
            </div>
            <div style={{ flex: 0.25, flexDirection: 'column', display: 'flex' }}>
              <button type="button" onClick={handleVerifyPhoneOtp} className="otp-button">Verify OTP</button>
            </div>
          </div>
        )}
        */}

        <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: '15px' }}>
          <div style={{ flex: 0.2, flexDirection: 'column', display: 'flex' }}>
            <label htmlFor="message">Message</label>
          </div>
          <div style={{ flex: '1 1 0', minWidth: 0, flexDirection: 'column', display: 'flex' }}>
            <textarea
              id="message"
              name="message"
              rows="4"
              value={formData.message}
              onChange={handleChange}
              className={errors.message ? 'input-error' : ''}
              style={{ width: '90%' }}
            />
            {errors.message && <div className="error-message">{errors.message}</div>}
          </div>
        </div>
        {/* Captcha row */}
        <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: '15px' }}>
          <div style={{ flex: 0.2, flexDirection: 'column', display: 'flex' }}>
            <label htmlFor="captcha">Captcha</label>
          </div>
          <div style={{ flex: '1 1 0', minWidth: 0, flexDirection: 'column', display: 'flex' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: "row", flex: 1, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: "column", flex: 0.5 }}>
                <div style={{ fontWeight: 600 }}>{captchaQuestion}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: "column", flex: 0.5 }}>

                <button
                  type="button"
                  className="otp-button"
                  onClick={() => {
                    const a = Math.floor(Math.random() * 10) + 1;
                    const b = Math.floor(Math.random() * 10) + 1;
                    setCaptchaQuestion(`${a} + ${b} = ?`);
                    setCaptchaAnswer(String(a + b));
                    setCaptchaInput('');
                  }}
                >
                  Refresh
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: "row", flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: "column", flex: 1 }}>
                <input
                  id="captcha"
                  name="captcha"
                  type="text"
                  value={captchaInput}
                  onChange={(e) => {
                    setCaptchaInput(e.target.value);
                    handleChange(e);
                  }}
                  className={errors.captcha ? 'input-error' : ''}
                  style={{ width: '90%' }}
                  maxLength={3}
                  placeholder={captchaQuestion}
                />
                {errors.captcha && <div className="error-message">{errors.captcha}</div>}
              </div>
            </div>
          </div>
        </div>

        {submitted && <div className="success-message">Thank you for your message!<br />We will get back to you shortly.</div>}
        {submitError && <div className="error-message">{submitError}</div>}
        <div className="contact-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
